import { FormEvent, useEffect, useState } from "react";
import { ArrowLeft, Eye, Save, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { AdminUser, getCurrentAdmin, logout } from "./api";
import {
  NewsCategory,
  NewsItem,
  NewsItemPayload,
  getAdminNewsItem,
  listAdminNewsCategories,
  saveAdminNewsItem,
  uploadAdminNewsImage,
} from "@/services/news";
import { AdminSwitchCard } from "./components/AdminChoiceControl";
import { AdminFormNotice } from "./components/AdminFormNotice";
import { AdminRichTextEditor } from "./components/AdminRichTextEditor";

type FormState = {
  Slug: string;
  Title: string;
  NewsCategoryId: string;
  Content: string;
  PublishedAt: string;
  IsPublished: boolean;
  SortOrder: string;
};

const emptyForm: FormState = {
  Slug: "",
  Title: "",
  NewsCategoryId: "",
  Content: "",
  PublishedAt: new Date().toISOString().slice(0, 10),
  IsPublished: true,
  SortOrder: "100",
};

function RequiredMark() {
  return <span className="ml-1 text-[#c3423f]" aria-hidden="true">*</span>;
}

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\p{L}\p{N}-]+/gu, "")
    .replace(/^-+|-+$/g, "");
}

function itemToForm(item: NewsItem): FormState {
  return {
    Slug: item.slug,
    Title: item.title,
    NewsCategoryId: item.newsCategoryId ? String(item.newsCategoryId) : "",
    Content: item.content || "",
    PublishedAt: item.date || "",
    IsPublished: item.isPublished,
    SortOrder: String(item.sortOrder || 100),
  };
}

function NewsPreviewOverlay({ form, categoryName, onClose }: { form: FormState; categoryName: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[80] overflow-auto bg-white">
      <div className="sticky top-0 z-10 border-b border-[#dfe6e1] bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#9a8a7a]">最新消息預覽</div>
            <h2 className="text-xl font-bold text-[#1B3D2A]">{form.Title || "未命名消息"}</h2>
          </div>
          <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={onClose}>
            <X size={18} />
            關閉預覽
          </button>
        </div>
      </div>

      <main className="bg-white">
        <div className="mx-auto max-w-4xl px-6 py-8 md:px-10 md:py-12 lg:px-16">
          <div className="flex items-center gap-2 text-sm text-[#9a8a7a]">
            <span>首頁</span>
            <span>{'>'}</span>
            <span>最新消息</span>
            <span>{'>'}</span>
            <span>{categoryName || "分類"}</span>
          </div>
          <h1 className="mt-4 text-2xl font-bold leading-snug text-[#1B3D2A] md:text-3xl">{form.Title || "消息標題"}</h1>
          <p className="mt-2 text-sm text-[#9a8a7a]">{form.PublishedAt || "未設定日期"}</p>
          <div className="my-6 h-px bg-[#E8E0D6]" />
          <div
            className="prose prose-lg max-w-none text-gray-700 prose-headings:text-[#1B3D2A] prose-a:text-[#1B3D2A] prose-img:rounded-xl prose-iframe:aspect-video prose-iframe:w-full prose-iframe:rounded-xl"
            dangerouslySetInnerHTML={{ __html: form.Content || "<p>消息內容預覽</p>" }}
          />
        </div>
      </main>
    </div>
  );
}

export default function AdminNewsItemForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const newsItemId = id ? Number(id) : null;
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [loading, setLoading] = useState(Boolean(newsItemId));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    async function init() {
      try {
        const me = await getCurrentAdmin();
        if (!alive) return;
        setCurrentUser(me.User);
        const categoryRows = await listAdminNewsCategories();
        if (alive) setCategories(categoryRows.filter((item) => item.isActive));
        if (newsItemId) {
          const item = await getAdminNewsItem(newsItemId);
          if (alive) setForm(itemToForm(item));
        }
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "最新消息資料載入失敗。");
        navigate("/admin-login", { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    }
    void init();
    return () => {
      alive = false;
    };
  }, [newsItemId, navigate]);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function setField<Key extends keyof FormState>(key: Key, value: FormState[Key]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function handleTitleChange(value: string) {
    setForm((current) => ({
      ...current,
      Title: value,
      Slug: current.Slug || slugify(value),
    }));
  }

  function validate() {
    const errors: string[] = [];
    if (!form.Title.trim()) errors.push("請輸入消息標題。");
    if (!form.Slug.trim()) errors.push("請輸入網址代稱。");
    if (!form.NewsCategoryId.trim()) errors.push("請選擇最新消息分類。");
    if (!form.Content.replace(/<[^>]*>/g, "").trim()) errors.push("請輸入消息內容。");
    return errors;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const errors = validate();
    if (errors.length > 0) {
      setMessage(errors[0]);
      return;
    }
    setSaving(true);
    setMessage("");
    const payload: NewsItemPayload = {
      Slug: slugify(form.Slug),
      Title: form.Title.trim(),
      NewsCategoryId: Number(form.NewsCategoryId),
      Content: form.Content.trim(),
      PublishedAt: form.PublishedAt ? `${form.PublishedAt} 00:00:00` : null,
      IsPublished: form.IsPublished,
      SortOrder: Number(form.SortOrder || 100),
    };

    try {
      const saved = await saveAdminNewsItem(newsItemId, payload);
      navigate(`/admin/news-items/${saved.id}/edit`, { replace: true });
      setMessage("最新消息已儲存。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "最新消息儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  const selectedCategoryName = categories.find((item) => String(item.id) === form.NewsCategoryId)?.name || "";

  return (
    <AdminLayout sectionLabel="內容管理" title={newsItemId ? "編輯最新消息" : "新增最新消息"} user={currentUser} onLogout={handleLogout}>
      <form onSubmit={handleSubmit}>
        <div className="sticky top-[76px] z-10 mb-5 rounded-md border border-[#dfe6e1] bg-white/95 p-3 shadow-[0_10px_24px_rgba(32,61,50,.08)] backdrop-blur md:top-[110px]">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => navigate("/admin/news-items")} type="button">
              <ArrowLeft size={18} />
              回消息列表
            </button>
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => setPreviewOpen((current) => !current)} type="button">
                <Eye size={18} />
                {previewOpen ? "關閉預覽" : "預覽"}
              </button>
              <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving || loading} type="submit">
                <Save size={18} />
                {saving ? "儲存中" : "儲存消息"}
              </button>
            </div>
          </div>
        </div>

        <AdminFormNotice message={message} />

        {loading ? (
          <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-10 text-center text-[#59665f]">載入中...</div>
        ) : (
          <div className="grid gap-5 2xl:grid-cols-[minmax(0,1fr)_400px]">
            <div className="space-y-5">
              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">基本資訊</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="grid gap-1 md:col-span-2">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="news-title">消息標題<RequiredMark /></label>
                    <input id="news-title" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Title} onChange={(event) => handleTitleChange(event.target.value)} />
                  </div>
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="news-slug">網址代稱 Slug<RequiredMark /></label>
                    <input id="news-slug" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Slug} onChange={(event) => setField("Slug", slugify(event.target.value))} placeholder="store-notice-2026" />
                  </div>
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="news-category">最新消息分類<RequiredMark /></label>
                    <select
                      id="news-category"
                      className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                      value={form.NewsCategoryId}
                      onChange={(event) => setField("NewsCategoryId", event.target.value)}
                    >
                      <option value="">請選擇分類</option>
                      {categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </select>
                  </div>
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="news-date">發布日期</label>
                    <input id="news-date" type="date" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.PublishedAt} onChange={(event) => setField("PublishedAt", event.target.value)} />
                  </div>
                </div>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">消息內容<RequiredMark /></h2>
                <AdminRichTextEditor value={form.Content} onChange={(value) => setField("Content", value)} onUploadImage={uploadAdminNewsImage} imageAlt="消息內容圖片" />
              </section>
            </div>

            <aside className="space-y-5">
              <section className="rounded-md sticky top-40 z-10 border border-[#dfe6e1] bg-white p-4 md:top-40">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">發布設定</h2>
                <div className="grid gap-3">
                  <AdminSwitchCard checked={form.IsPublished} description="開啟後會顯示在官網最新消息列表與詳情頁。" label="上架消息" onChange={(checked) => setField("IsPublished", checked)} />
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="news-sort">排序</label>
                    <input id="news-sort" type="number" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.SortOrder} onChange={(event) => setField("SortOrder", event.target.value)} />
                  </div>
                </div>
              </section>
            </aside>
          </div>
        )}
      </form>
      {previewOpen && <NewsPreviewOverlay categoryName={selectedCategoryName} form={form} onClose={() => setPreviewOpen(false)} />}
    </AdminLayout>
  );
}
